/**
 * One executive lens.
 *
 * Six of these are the dashboard. The shape is uniform — icon, figure, state, reason —
 * because a chairman comparing six cards should not have to re-learn the layout at each one.
 *
 * **Meaning is never carried by colour alone.** Every card renders a coloured mark *and* the
 * state word the API returned. That is not only an accessibility rule: this dashboard gets
 * printed, projected, and photographed on a phone in a meeting room, and a red dot that has
 * become a grey dot has to still say "late".
 *
 * The icon tile carries the card's tone as a tint, so the six read as a set at a glance and
 * the one in trouble is findable before any of them is read. It is decorative — the state
 * word underneath is what actually says the answer — so it is hidden from assistive tech
 * rather than given a label that would be read out twice.
 */
import type { CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'

import type { Lens as LensData } from '@/api/types'
import { Icon, type IconName } from '@/components/Icon'

/**
 * Which glyph belongs to which lens.
 *
 * Keyed to `LensKey` on the API. Falls back rather than throwing on an unknown key: a lens
 * we add server-side should appear with a generic mark, not take the dashboard down.
 */
const GLYPH: Record<string, IconName> = {
  delivery: 'projects',
  financial: 'money',
  risk: 'warning',
  progress: 'chart',
  compliance: 'reports',
  governance: 'applications',
}

/**
 * Sentence case for a state the API sends as data.
 *
 * `"late"` → `"Late"`, `"within budget"` → `"Within budget"`. In the component rather than in
 * CSS: `text-transform: capitalize` would give "Within Budget", which is title case and
 * belongs to headings, and `::first-letter` does not apply to a flex container — which the
 * state row is, because it carries the coloured mark beside the word.
 *
 * A no-op in Arabic, which has no letter case. Nothing here assumes otherwise.
 */
function sentence(value: string): string {
  return value ? value[0]!.toUpperCase() + value.slice(1) : value
}

export function Lens({
  lens,
  onOpen,
  index = 0,
}: {
  lens: LensData
  onOpen?: (key: string) => void
  index?: number
}) {
  const { t } = useTranslation()
  const label = t(`lens.${lens.key}`, lens.key)

  const body = (
    <>
      <div className="lens__top">
        <span className="lens__glyph" aria-hidden="true">
          <Icon name={GLYPH[lens.key] ?? 'chart'} size={18} />
        </span>
        {onOpen ? (
          <span className="lens__arrow" aria-hidden="true">
            <Icon name="open" size={14} strokeWidth={2} />
          </span>
        ) : null}
      </div>

      <p className="lens__figure">
        <span className="lens__headline numeric">{lens.headline}</span>
        {lens.unit ? <span className="lens__unit">{lens.unit}</span> : null}
      </p>

      <p className="lens__label">{label}</p>

      <p className={`lens__state lens__state--${lens.tone}`}>
        <span className="lens__mark" aria-hidden="true" />
        {sentence(lens.state)}
      </p>

      <p className="lens__detail">{lens.detail}</p>
    </>
  )

  // Each card enters a beat after the one before it, so the row assembles left to right
  // instead of appearing all at once. Six simultaneous arrivals read as a flash.
  const style = { '--lens-delay': `${index * 0.06}s` } as CSSProperties

  // A card that does nothing is not a button. Announcing one as clickable and then ignoring
  // the click is worse than rendering plain text.
  if (!onOpen) {
    return (
      <article className="lens" data-tone={lens.tone} data-lens={lens.key} style={style}>
        {body}
      </article>
    )
  }

  return (
    <article className="lens lens--open" data-tone={lens.tone} data-lens={lens.key} style={style}>
      <button
        type="button"
        className="lens__button"
        onClick={() => onOpen(lens.key)}
        aria-label={`${label}: ${lens.headline} ${lens.unit ?? ''} — ${lens.state}`}
      >
        {body}
      </button>
    </article>
  )
}
